import React, { ReactElement, useState } from "react";
import { Form } from "react-bootstrap";
import { RiPauseLine, RiPlayLine, RiStopLine } from "@remixicon/react";
import useKeyboardShortcuts from "../../../contexts/KeyboardShortcutContext";
import { Ticket } from "../../../contexts/selection-state/selection-state";
import useSelectionState from "../../../contexts/selection-state/SelectionStateContext";
import { ChatStatus, useHoldChatsMutation, useUnholdChatsMutation } from "../../../__generated__/api";
import Confirm from "../../confirm/Confirm";
import { useRegisterAction, useTicketState } from "../../../contexts/ticket-state";
import {
    ActionContext, ActionHandler, ActionType, HANDLER_INTERFACE,
} from "../../../contexts/ticket-state/action-registry";
import { useTheme } from "../../../contexts/Theme";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";

export default function Hold(): ReactElement {
    const selectionState = useSelectionState();
    const [isShow, setShouldShow] = useState(false);
    const [holdChatsMutation] = useHoldChatsMutation();
    const [unholdChatsMutation] = useUnholdChatsMutation();

    const [notes, setNotes] = useState("");
    const [actionHandler, setActionHandler] = useState<ActionHandler>();
    const ticketContext = useTicketState();

    const theme = useTheme();
    const [lastOnHold, setLastOnHold] = useState<Ticket[]>([]);

    const hold = (tickets?: Ticket[]) => {
        const selection = tickets
            ?? ticketContext?.selectionState.current?.toIdList("chat")
            ?? selectionState
                .getSelection()
                .filter(ticket => ticket.status === ChatStatus.Assigned);

        setLastOnHold(selection);
        setShouldShow(true);
    };

    const doHold = async (note: string) => {
        setShouldShow(false);

        actionHandler?.items.forEach(ticket => ticket.setStatusLocal(ChatStatus.OnHold));
        actionHandler?.done();
        const ids = lastOnHold.toIdList("id") as string[];
        await holdChatsMutation({
            variables: {
                ids,
                options: {
                    note,
                },
            },
        });

        selectionState.closeCurrent();
    };

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "k+h",
            context: "default",
            description: "Hold the conversation",
            group: "Conversations",
            handler: () => {
                hold();
                setActionHandler(ticketContext?.selectionState.createActionHandler(ActionContext.BULK_ACTION));
            },
        });
    });

    useRegisterAction({
        createBulkActionButton(items) {
            return items.some(row => row.status !== ChatStatus.OnHold) ? {
                text: "Hold",
                icon: <RiPauseLine />,
                variant: "ghost-gray",
            } : undefined;
        },
        handleBulkAction(handler) {
            setActionHandler(handler);
            hold(handler.items.map(row => row.chat));
        },
    }, ActionType.HOLD);

    useRegisterAction({
        createBulkActionButton(items) {
            return items.some(row => row.status === ChatStatus.OnHold) ? {
                text: "Stop Hold",
                icon: <RiStopLine />,
                variant: "ghost-gray",
            } : undefined;
        },
        handleBulkAction(handler) {
            setActionHandler(handler);
            handler.items.forEach(ticket => ticket.setStatusLocal(ChatStatus.Assigned));
            handler.done();
            LoggingHandler.instance.wrap(async () => {
                await unholdChatsMutation({
                    variables: {
                        ids: handler.items.toIdList("id"),
                        options: {},
                    },
                });
            });
        },
    }, ActionType.STOP_HOLD);

    selectionState.registerHold(tickets => {
        hold(tickets);
    });

    return (
        <Confirm
            shouldShow={isShow}
            title="Put on Hold"
            onHide={() => {
                setShouldShow(false);
                actionHandler?.rollback();
            }}
            confirmText="Hold"
            onConfirm={() => {
                doHold(notes);
            }}
        >
            <div>
                <Form.Group>
                    <Form.Label className={theme.isV3 ? "text-body-2-regular" : "fw-bolder"}>
                        Notes
                        {" "}
                        <small className={theme.isV3 ? "text-dim" : "text-light"}>(optional)</small>
                    </Form.Label>

                    <Form.Control
                        as="textarea"
                        value={notes}
                        onChange={evt => setNotes(evt.target.value)}
                    />
                    <Form.Text className="d-block">
                        Any notes you want to add before holding
                    </Form.Text>
                </Form.Group>
            </div>
        </Confirm>
    );
}

HANDLER_INTERFACE.register(Hold);

Hold.defaultProps = {
    refreshData: () => {
        // nothing
    },
};
